import { AfterViewInit, Component, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchModule, SearchComponent, SearchState } from '@nx-ahs/search';
import { UtilitiesService } from '@nx-ahs/shared/services';
import { PageHeaderComponent, ProgressButtonComponent, DbIconComponent } from '@nx-ahs/shared/components';
import { MomentPipe } from '@nx-ahs/shared/pipes';
import { Subscription } from 'rxjs';
import { MailService } from '../services/mail.service';
import { ViewPdfComponent } from '../view-pdf/view-pdf.component';

@UntilDestroy()
@Component({
	selector: 'ahs-return-mail-single',
	templateUrl: './return-mail-single.component.html',
	styleUrls: ['./return-mail-single.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatDatepickerModule,
		MatButtonModule,
		MatCardModule,
		SearchModule,
		PageHeaderComponent,
		ProgressButtonComponent,
		DbIconComponent,
		MomentPipe
	]
})
export class ReturnMailSingleComponent implements OnInit, AfterViewInit {
	@ViewChild('returnMailSearch') returnMailSearch: SearchComponent;

	//#region Public Members
	public letterDetails: any;
	public maxReturnedDate: Date = new Date();
	public maxPostmarkedDate: Date = new Date();
	public returnMailReasons: any[] = [];
	public returnMailSingleForm: FormGroup;
	public saving: boolean = false;
	public showReturnMailSingleForm: boolean = false;
	public states: any[] = [];
	//#endregion Public Members

	//#region Private Members
	private criteriaFormSubscription: Subscription;

	//#endregion

	//#region Constructor
	constructor(
		private formBuilder: FormBuilder,
		private mailService: MailService,
		private dialogService: MatDialog,
		private utilitiesService: UtilitiesService,
		private snackBar: MatSnackBar
	) {}
	//#endregion Constructor

	//#region Lifecycle Events
	ngAfterViewInit(): void {
		this.setupCriteriaFormSubscription();
	}

	ngOnInit(): void {
		this.getRefData();
		this.buildForms();
	}
	//#endregion Lifecycle Events

	//#region Public Methods
	public backToSearch(): void {
		this.showReturnMailSingleForm = false;
	}

	public async processReturnMail(): Promise<any> {
		this.saving = true;
		this.utilitiesService.markAllFieldsTouched(this.returnMailSingleForm);
		this.utilitiesService.validateForm(this.returnMailSingleForm);
		if (this.returnMailSingleForm.valid) {
			const returnedDate = this.returnMailSingleForm.get('returnedDate').value;
			const postmarkedDate = this.returnMailSingleForm.get('postmarkedDate').value;
			const returnMailReasonId = this.returnMailSingleForm.get('returnMailReasonId').value;
			const resendTo_Name = this.returnMailSingleForm.get('resendTo_Name').value;
			const resendTo_AddressLine1 = this.returnMailSingleForm.get('resendTo_AddressLine1').value;
			const resendTo_AddressLine2 = this.returnMailSingleForm.get('resendTo_AddressLine2').value;
			const resendTo_City = this.returnMailSingleForm.get('resendTo_City').value;
			const resendTo_StateId = this.returnMailSingleForm.get('resendTo_StateId').value;
			const resendTo_Zip = this.returnMailSingleForm.get('resendTo_Zip').value;
			await this.mailService.returnMail_ProcessReturn(
				this.letterDetails.letterId,
				returnedDate,
				postmarkedDate,
				returnMailReasonId,
				resendTo_Name,
				resendTo_AddressLine1,
				resendTo_AddressLine2,
				resendTo_City,
				resendTo_StateId,
				resendTo_Zip
			);
			this.snackBar.open('Return Mail processed successfully', 'DISMISS', { duration: 5000 });
			this.reset();
		}
		this.saving = false;
	}

	public async showReturnMailForm(event: any): Promise<any> {
		this.buildForms();
		const letterId = event.letterId;
		this.letterDetails = await this.mailService.returnMail_GetLetterDetails(letterId);
		if (this.letterDetails.status.toLowerCase().trim() === 'returned') {
			this.snackBar.open('Cannot process mail that has already been returned', 'DISMISS', { duration: 5000 });
		} else {
			this.showReturnMailSingleForm = true;
		}
	}

	public viewMailFile(): void {
		this.dialogService.open(ViewPdfComponent, {
			disableClose: true,
			minWidth: '900px',
			panelClass: 'default-modal',
			data: {
				mailFileId: this.letterDetails.mailFileId
			}
		});
	}
	//#endregion Public Methods

	//#region Private Methods
	private addressValidator(control: AbstractControl): ValidationErrors {
		const formGroup = control.parent;
		if (formGroup) {
			let anotherControlHasValue: boolean = false;
			const keys = Object.keys(formGroup.controls).filter((f) => {
				return f !== 'returnedDate' && f !== 'postmarkedDate' && f !== 'returnMailReasonId';
			});
			for (const key of keys) {
				const currentControl = formGroup.get(key);
				if (currentControl != control) {
					anotherControlHasValue =
						currentControl.value !== undefined &&
						currentControl.value !== null &&
						currentControl.value !== '';
					if (anotherControlHasValue) {
						break;
					}
				}
			}
			if (anotherControlHasValue && !control.value) {
				return { required: true };
			}
		}
		return null;
	}

	private buildForms(): void {
		this.returnMailSingleForm = this.formBuilder.group({
			returnedDate: [new Date(), Validators.required],
			postmarkedDate: [new Date(), Validators.required],
			returnMailReasonId: [null, Validators.required],
			resendTo_Name: [null, this.addressValidator],
			resendTo_AddressLine1: [null, this.addressValidator],
			resendTo_AddressLine2: [null],
			resendTo_City: [null, this.addressValidator],
			resendTo_StateId: [null, this.addressValidator],
			resendTo_Zip: [null, this.addressValidator]
		});
	}

	private getRefData(): Promise<any>[] {
		const promises: Promise<any>[] = [];
		promises.push(
			this.mailService
				.getReturnMailReasons()
				.toPromise()
				.then((results) => {
					this.returnMailReasons = results;
				})
		);

		promises.push(
			this.mailService
				.getStates()
				.toPromise()
				.then((results) => {
					this.states = results;
				})
		);

		return promises;
	}

	private reset() {
		this.returnMailSearch.clear();
		this.returnMailSearch.resetState();
		this.showReturnMailSingleForm = false;
		this.buildForms();
	}

	private setupCriteriaFormSubscription() {
		if (!this.criteriaFormSubscription) {
			this.criteriaFormSubscription = this.returnMailSearch.criteriaFormInitialized
				.pipe(untilDestroyed(this))
				.subscribe(() => {
					let lastIdentificationBarcode: string = ''; // added because calling returnMailSearch.search() will always disable and then reenable the criteriaForm, which always triggers a criteriaForm.valueChanges, causing an infinite loop. Added this local variable to check if the value of identificationBarcode has changed since the last time returnMail.search() was run, and if not then it doesn't run it again.

					this.returnMailSearch.searchState.criteriaForm.valueChanges.subscribe((value) => {
						if (value && value.identificationBarcode) {
							if (
								value.identificationBarcode.length == 16 &&
								value.identificationBarcode !== lastIdentificationBarcode
							) {
								lastIdentificationBarcode =
									this.returnMailSearch.searchState.criteriaForm.controls.identificationBarcode.value;
								this.returnMailSearch.search();
							}
						}
					});
				});
		}
	}
	//#endregion Private Methods
}
